// Authenticated download of the briefing report as an Excel workbook (one
// sheet per section: totals, channels, lines, weekly, daily, breakdowns; the
// numbers are numeric cells). The endpoint requires the Authorization header,
// so a plain <a href> won't work — same fetch+blob approach as the OC history
// export (src/utils/oc/exportOcCsv.ts).

import { parseCsvFilename, triggerBlobDownload } from "../csvDownload";

const FALLBACK_FILENAME = "report.xlsx";

/** Carries the HTTP status so the caller can pick an error message. */
export class BriefingReportDownloadError extends Error {
  status: number;

  constructor(status: number, message?: string) {
    super(message || `Report download failed with status ${status}`);
    this.name = "BriefingReportDownloadError";
    this.status = status;
  }
}

/** Optional report period, YYYY-MM-DD, both ends inclusive. */
export interface ReportPeriod {
  startDate?: string;
  endDate?: string;
}

/**
 * Downloads the briefing report workbook, saving it under the filename the
 * backend sends in Content-Disposition (`Report_<campaña>_<mes>_<año>.xlsx`).
 * Throws BriefingReportDownloadError on a non-OK response.
 *
 * Without a period the report covers the whole life of each line.
 */
export const exportBriefingReport = async (
  briefingId: string,
  token: string,
  period: ReportPeriod = {}
): Promise<void> => {
  const params = new URLSearchParams();
  if (period.startDate) params.set("startDate", period.startDate);
  if (period.endDate) params.set("endDate", period.endDate);
  const qs = params.toString();
  const url = `${process.env.REACT_APP_API_BACKEND_URL}/api/briefings/${briefingId}/report/xlsx${qs ? `?${qs}` : ""}`;

  const response = await fetch(url, {
    method: "GET",
    headers: { Authorization: `Bearer ${token}` },
  });

  if (!response.ok) {
    let message: string | undefined;
    try {
      message = (await response.json())?.message;
    } catch {
      // Non-JSON error body — fall back to the status-based message.
    }
    throw new BriefingReportDownloadError(response.status, message);
  }

  const blob = await response.blob();
  const filename = parseCsvFilename(
    response.headers.get("Content-Disposition"),
    FALLBACK_FILENAME
  );
  triggerBlobDownload(blob, filename);
};
